import { useLazyQuery, useMutation } from '@apollo/client/react';
import { formatISO } from 'date-fns';
import { useAtom } from 'jotai';
import { useCallback, useEffect } from 'react';

import {
  FetchMedixsReceptionDocument,
  GetMedixsAppointmentDocument,
  GetMedixsReceptionsDocument,
  MedixsModalMedixsReceptionFragment,
} from '~/graphql/generated/graphql';
import { getNode } from '~/graphql/utility';
import { medixsReceptionIdentificationModalState } from '~/state/partials/medixs_reception_identification_modal/atoms';

type MedixsReceptions = {
  loading: boolean;
  medixsReceptions: MedixsModalMedixsReceptionFragment[];
};

export const useFetchMedixsReceptions = (): MedixsReceptions => {
  const [{ isOpen, appointmentId, medixsSettingId, patientId, selectedDate }, setState] = useAtom(
    medixsReceptionIdentificationModalState,
  );
  const [fetchMedixsReception, { loading: fetchMedixsReceptionLoading, error: fetchMedixsError }] =
    useMutation(FetchMedixsReceptionDocument);

  const [
    getAppointment,
    { data: appointmentData, loading: appointmentLoading, error: appointmentError },
  ] = useLazyQuery(GetMedixsAppointmentDocument, {
    fetchPolicy: 'network-only',
  });
  const [getMedixsReceptions, { data, loading }] = useLazyQuery(GetMedixsReceptionsDocument, {
    fetchPolicy: 'network-only',
  });
  const fetchMedixsReceptions = useCallback(
    async (patientId: string) => {
      const date = selectedDate ? formatISO(selectedDate, { representation: 'date' }) : undefined;

      if (!date) return;

      await fetchMedixsReception({
        variables: {
          input: {
            date,
            startAt: '00:00', // 選び直した日付の午前中の受付も検索対象にする
            endAt: '23:59', // 必ず指定日の最終時分までを検索範囲にする
          },
        },
      });

      if (medixsSettingId) {
        // fetchが終わってから受付リスト取得
        getMedixsReceptions({
          variables: {
            medixsSettingId,
            patientId,
            date,
          },
        });
      }
    },
    [fetchMedixsReception, getMedixsReceptions, medixsSettingId, selectedDate],
  );

  const medixsReceptions = getNode(data, 'MedixsSetting')?.medixsReceptions.nodes || [];
  const appointment = getNode(appointmentData, 'Appointment');
  const createdAt = appointment?.createdAt.split('T')[0];

  useEffect(() => {
    if (createdAt)
      setState((_state) => ({
        ..._state,
        selectedDate: new Date(createdAt),
      }));
  }, [createdAt, setState]);

  useEffect(() => {
    if (isOpen && appointmentId) {
      getAppointment({
        variables: {
          appointmentId,
        },
      });
    }
  }, [appointmentId, getAppointment, isOpen]);

  useEffect(() => {
    if (patientId && appointment?.createdAt) {
      fetchMedixsReceptions(patientId);
    }
  }, [appointment, fetchMedixsReceptions, patientId]);

  useEffect(() => {
    if (fetchMedixsError) {
      console.log(fetchMedixsError);
    }
  }, [fetchMedixsError]);

  useEffect(() => {
    if (appointmentError) {
      setState((_state) => ({
        ..._state,
        error: appointmentError.message,
      }));
    }
  }, [appointmentError, setState]);

  return {
    loading: appointmentLoading || fetchMedixsReceptionLoading || loading,
    medixsReceptions,
  };
};
